/**
 * Zustand ACL Store
 * 
 * Global state management for ACL data using Zustand.
 * Can be accessed both inside and outside React components.
 * Used by API interceptors to add user headers and React components for reactivity.
 */

import { create } from 'zustand';
import { UserInfo } from '@/shared/types/acl';

/**
 * ACL store state interface
 */
export interface AclStoreState {
  /** Current user information from ACL */
  userInfo: UserInfo | null;
  /** Set user info */
  setUserInfo: (userInfo: UserInfo | null) => void;
  /** Get user ID (convenience method) */
  getUserId: () => string | null;
  /** Get user group ID (convenience method) */
  getUserGroupId: () => number | null;
  /** CSRF token string */
  csrfToken: string | null;
  /** CSRF token expiration as ISO datetime string */
  csrfTokenExpiration: string | null;
  /** Store a new CSRF token */
  setCsrfToken: (token: string, expiration: string) => void;
  /** Clear CSRF token data */
  clearCsrfToken: () => void;
  /** Clear all ACL data (e.g., on logout) */
  clear: () => void;
}

/**
 * Zustand ACL Store
 * 
 * @example
 * // In React components (reactive):
 * const userInfo = useAclStore((state) => state.userInfo);
 * const userId = useAclStore((state) => state.getUserId());
 * 
 * // Outside React (non-reactive):
 * const userId = useAclStore.getState().getUserId();
 * const userGroupId = useAclStore.getState().getUserGroupId();
 */
export const useAclStore = create<AclStoreState>((set, get) => ({
  userInfo: null,

  setUserInfo: (userInfo) => set({ userInfo }),

  getUserId: () => {
    const { userInfo } = get();
    return userInfo?.user_id ?? null;
  },

  getUserGroupId: () => {
    const { userInfo } = get();
    return userInfo?.user_group_id ?? null;
  },

  csrfToken: null,
  csrfTokenExpiration: null,

  setCsrfToken: (token, expiration) =>
    set({ csrfToken: token, csrfTokenExpiration: expiration }),

  clearCsrfToken: () => set({ csrfToken: null, csrfTokenExpiration: null }),

  clear: () => set({ userInfo: null, csrfToken: null, csrfTokenExpiration: null }),
}));
